import { getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import Paths from '@/constants/Paths';
import store from '@/store';
import router from '@/router';
import { SystemConfigurationDto, PublicSystemConfigurationDto } from '@/api';
import { PaymentGatewayTypes } from '@/constants/PaymentGatewayTypes';
import { Currencies } from '@common/core/Currency';
import { MIGRATION_FLAGS } from '@T-system/common/src/api/constants';

const _systemConfiguration: SystemConfigurationDto = {
  id: '',
  eshopMultiLocalizationEnabled: false,
  cardApplicationFilesUploadEnabled: false,
  virtualCardEWalletEnabled: false,
  eshopFooterManualEnabled: false,
  SMTPPermeabilityEnabled: false,
  bankCardAsCarrier: false,
  virtualCardAsCarrier: false,
  showTaxOnReceipt: false,
  ticketMinValidFrom: '',
  haulierTokenKey: '',
  refundReasonEnabled: false,
  showSimpleCardApplication: false,
  allowFreeCardApplication: false,
  paymentGateway: '',
  EETLocalization: '',
  currency: '',
  eshopAllowedPaymentTypes: [],
  counterAllowedPaymentTypes: [],
  allowCameraInCardApplication: false,
  allowShortTermTickets: false,
  defaultLanguage: '',
  migrationFlags: [],
  haulierCardAsCarrier: false,
  marketingConsentCardEnabled: false,
  addressStreetRequired: false,
  addressStreetNumberRequired: false,
  addressCityRequired: false,
  addressZipCodeRequired: false,
  ticketCancellationFinalizationEnabled: false,
  priceListUseActualDateInsteadValidFrom: false,
  eshopReclamationEnabled: false,
  synchronizationWithExternalEnabled: false,
  allowIsic: false,
  assignCardWithoutAgeRestriction: false,
  allowCustomerCardGroup: false,
  haulierCardWLAsCarrier: false,
  allowBankCardApplicationFromCounter: false,
  allowCloseEshopToastOnlyUsingButton: false,
  allowPersistEshopSideMenu: false,
  allowFirstCardApplicationWithDiscountProfileForFree: false,
  disableDeleteAccountWithActiveTicketsAndEP: false,
  allowUpdateCardValidity: false,
  allowSetDefaultProfileValidFromByCardValidFrom: false,
  allowCustomerCreationUnder15Years: false,
  allowCustomerRegistrationUnder15Years: false,
  allowCustomerProfileApplication: false,
  allowGoogleLogin: false,
  allowFacebookLogin: false,
  allowRewardProgram: false,
  allowTicketCancellation: false,
  allowExpressCardProduction: false,
  allowExternalCards: false,
  allowWhitelists: false,
  allowPickupInPostOffice: false,
  allowPaperCards: false,
  tariffCoreType: SystemConfigurationDto.tariffCoreType.ZONE,
  allowSmartCardWithoutStructure: false,
  allowClearing: false,
  customerProfileLimit: 2,
  allowOffenses: true,
  allowThirdPartyTickets: false,
};

export interface SystemConfigurationPayload {
  redirect: boolean;
  systemConfiguration: SystemConfigurationDto | PublicSystemConfigurationDto;
}

@Module({ dynamic: true, store, namespaced: true, name: 'systemConfiguration' })
class SystemConfiguration extends VuexModule {
  public configuration: SystemConfigurationDto = _systemConfiguration;

  public initialized = false;

  @Mutation
  public setSystemConfiguration(payload: SystemConfigurationPayload): void {
    this.configuration = { ...this.configuration, ...payload.systemConfiguration };
    this.initialized = true;

    if (payload.redirect) {
      const { redirectUrl, token } = router.currentRoute.value.query;
      if (redirectUrl) {
        const path = token ? `${redirectUrl.toString()}?token=${token}` : redirectUrl.toString();
        router.push(path);
      } else {
        router.push(Paths.USER_ACCOUNT);
      }
    }
  }

  @Mutation
  public clearSystemConfiguration(): void {
    this.configuration = _systemConfiguration;
  }

  public get allowedPaymentTypes() {
    return this.configuration.eshopAllowedPaymentTypes;
  }

  public get currency() {
    return Object.values(Currencies).find(currency => currency.Code === this.configuration.currency);
  }

  public get cardApplicationAttachmentAllowed() {
    return this.configuration.cardApplicationFilesUploadEnabled;
  }

  public get isCsobPaymentGateway() {
    return this.configuration.paymentGateway === PaymentGatewayTypes.CSOB;
  }

  public get isGpPaymentGateway() {
    return this.configuration.paymentGateway === PaymentGatewayTypes.GP;
  }

  public get isVirtualCardEWalletAllowed() {
    return this.configuration.virtualCardEWalletEnabled;
  }

  public get isAllowedFreeCardApplication() {
    return this.configuration.allowFreeCardApplication;
  }

  public get isBankCardAsCarrierAllowed() {
    return this.configuration.bankCardAsCarrier;
  }

  public get isVirtualCardAsCarrierAllowed() {
    return this.configuration.virtualCardAsCarrier;
  }

  public get isTelshopMigrationAllowed() {
    return (
      this.configuration.migrationFlags.length && this.configuration.migrationFlags.includes(MIGRATION_FLAGS.TELSHOP)
    );
  }

  public get customerCreationUnder15YearsAllowed() {
    return this.configuration.allowCustomerCreationUnder15Years;
  }

  public get isHaulierCardAsCarrierAllowed() {
    return this.configuration.haulierCardAsCarrier;
  }

  public get isIsicAllowed() {
    return this.configuration.allowIsic;
  }

  public get isSynchronizationWithExternalAllowed() {
    return this.configuration.synchronizationWithExternalEnabled;
  }

  public get isAssignCardWithoutAgeRestrictionAllowed() {
    return this.configuration.assignCardWithoutAgeRestriction;
  }

  public get isCloseToastUsingButtonAllowed() {
    return this.configuration.allowCloseEshopToastOnlyUsingButton;
  }

  public get isPersistSideMenuAllowed() {
    return this.configuration.allowPersistEshopSideMenu;
  }

  public get isDisableDeleteAccountAllowed() {
    return this.configuration.disableDeleteAccountWithActiveTicketsAndEP;
  }

  public get isCustomerProfileApplicationAllowed() {
    return this.configuration.allowCustomerProfileApplication;
  }

  public get registrationUnder15YearsAllowed() {
    return this.configuration.allowCustomerRegistrationUnder15Years;
  }

  get allowGoogleLogin() {
    return this.configuration.allowGoogleLogin;
  }

  get allowFacebookLogin() {
    return this.configuration.allowFacebookLogin;
  }

  get externalCardsAllowed() {
    return this.configuration.allowExternalCards;
  }

  get ticketCancellationAllowed() {
    return this.configuration.allowTicketCancellation;
  }

  get allowWhitelists() {
    return this.configuration.allowWhitelists;
  }

  get allowPersonalDetailsEdit() {
    return this.configuration.allowPersonalDetailsEdit;
  }

  get allowPersonalDetailsOptional() {
    return this.configuration.allowPersonalDetailsOptional;
  }

  get smartCardWithoutStructureAllowed() {
    return this.configuration.allowSmartCardWithoutStructure;
  }

  get allowOffenses() {
    return this.configuration.allowOffenses;
  }

  get isThirdPartyTicketAllowed() {
    return this.configuration.tariffCoreType === SystemConfigurationDto.tariffCoreType.RELATIONAL;
  }

  get allowThirdPartyTickets() {
    return this.configuration.allowThirdPartyTickets;
  }
}

export default getModule(SystemConfiguration);
